import React, { useMemo } from "react";
import useSWR from "swr";
import Button from "../../../Components/Common/Button";
import { Loader } from "../../../Components/Common/Loader";
import { useNavigate } from "react-router-dom";
import { publicUrls } from "../../../Api/Auth/AppUrls";
import { StaticPageApiUrls } from "../../../Api/Auth/ApiUrls";
import { noAuthfetcher } from "../../../Api/swrConfig";

const STATIC_PAGE_ROUTE = publicUrls.staticPage;
const DEFAULT_IMAGE =
  "https://storage.googleapis.com/road-to-fe/uploads/1741110062990-466da6c8-cc9b-4dc9-8e42-6012adcc460c-liveClass.webp";

const DEFAULT_FEATURES = [
  {
    id: "01",
    image: DEFAULT_IMAGE,
    title: "Live Online Class",
    subtitle:
      "Test Test Test Test Test Test Test Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test",
    link: publicUrls.LiveOnlineClass,
  },
  {
    id: "02",
    image:
      "https://storage.googleapis.com/road-to-fe/uploads/1741110100881-3352082b-c129-4963-b7ec-31bb27177882-tutorial.webp",
    title: "Tutorial",
    subtitle:
      "Test Test Test Test Test Test Test Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test",
    link: publicUrls.Tutorial,
  },
  {
    id: "03",
    image:
      "https://storage.googleapis.com/road-to-fe/uploads/1741110097103-a07d0770-80d0-495b-b90a-17c1ca349875-selfStudy.webp",
    title: "Self Study",
    subtitle:
      "Test Test Test Test Test Test Test Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test  Test Test Test Test Test Test",
    link: publicUrls.SelfStudy,
  },
];

const normalizeService = (service) => {
  if (!service) return null;

  let normalized = service;
  if (typeof service === "string") {
    const slug = service;
    normalized = {
      label: slug.replace(/-/g, " "),
      value: slug,
      slug,
    };
  }

  const value = normalized?.value || normalized?.slug;
  if (!value) return null;

  let parsedContent = {};
  if (normalized?.content) {
    try {
      parsedContent =
        typeof normalized.content === "string"
          ? JSON.parse(normalized.content)
          : normalized.content;
    } catch (error) {
      parsedContent = {};
    }
  }

  const title =
    normalized?.label ||
    normalized?.title ||
    parsedContent?.title ||
    value.replace(/-/g, " ");

  const subtitle =
    normalized?.description ||
    normalized?.subtitle ||
    parsedContent?.subtitle ||
    parsedContent?.headerDescription ||
    "";

  const image =
    normalized?.image ||
    parsedContent?.backgroundImage ||
    parsedContent?.image ||
    DEFAULT_IMAGE;

  return {
    id: value,
    title,
    subtitle,
    image,
    slug: value,
  };
};

const parseStaticContent = (content) => {
  if (!content) return {};
  try {
    return typeof content === "string" ? JSON.parse(content) : content;
  } catch (error) {
    return {};
  }
};

const fetchStaticPages = async ([, slugs]) => {
  if (!Array.isArray(slugs) || !slugs.length) {
    return {};
  }

  const responses = await Promise.all(
    slugs.map((slug) =>
      noAuthfetcher(StaticPageApiUrls.info(slug)).catch(() => null)
    )
  );

  return responses.reduce((acc, response, index) => {
    if (response?.data) {
      acc[slugs[index]] = response.data;
    }
    return acc;
  }, {});
};

const truncateText = (text, maxWords = 6) => {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const DeliveryMethods = ({ data }) => {
  const navigate = useNavigate();

  const services = useMemo(() => {
    if (!data?.data?.content) return [];
    try {
      const parsed = JSON.parse(data.data.content);
      if (!Array.isArray(parsed?.services)) return [];
      return parsed.services
        .map((service) => normalizeService(service))
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }, [data]);

  const isDynamic = services.length > 0;
  const features = isDynamic ? services : DEFAULT_FEATURES;

  const slugList = useMemo(() => {
    if (!isDynamic) return [];
    const unique = new Set(
      services.map((service) => service.slug).filter(Boolean)
    );
    return Array.from(unique);
  }, [isDynamic, services]);

  const {
    data: staticPages = {},
    isLoading: isStaticLoading,
  } = useSWR(
    slugList.length ? ["delivery-methods/static-pages", slugList] : null,
    fetchStaticPages
  );

  const enhancedFeatures = useMemo(() => {
    if (!isDynamic || !slugList.length) return features;

    return features.map((feature) => {
      const page = staticPages[feature.slug];
      if (!page) return feature;

      const parsed = parseStaticContent(page.content);

      return {
        ...feature,
        title: page.title || parsed?.heroTitle || feature.title,
        subtitle:
          page.headerDescription ||
          parsed?.heroSubtitle ||
          parsed?.subtitle ||
          parsed?.headerDescription ||
          feature.subtitle,
        image:
          parsed?.heroImage ||
          parsed?.backgroundImage ||
          page.image ||
          feature.image,
      };
    });
  }, [features, isDynamic, slugList.length, staticPages]);

  const resolveLink = (feature) => {
    if (!feature?.slug) return feature?.link || "#";
    return STATIC_PAGE_ROUTE.replace(":slug", feature.slug);
  };

  const isLoadingDynamic = isDynamic && (isStaticLoading || !staticPages);

  return (
    <div className="sm:mt-24 mt-12">
      <h2 className="text-3xl font-semibold text-primary text-center mt-4">
        Delivery Methods that Fit Your Learning Style
      </h2>
      <div className="pt-12">
        {isLoadingDynamic ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : (
          <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
            {(enhancedFeatures || features).map((item) => (
              <div
                key={item.id}
                className="rounded-xl overflow-hidden shadow-md bg-white border border-gray-100 flex flex-col"
              >
                <div className="relative">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-72 object-cover"
                  />
                </div>

                <div className="p-6 flex flex-col gap-4 flex-1">
                  <div>
                    <h3 className="text-2xl font-medium hover:text-primary duration-300 cursor-pointer truncate">
                      {truncateText(item.title, 5)}
                    </h3>
                    <p className="text-md text-gray-700 mt-2 truncate">
                      {truncateText(item.subtitle)}
                    </p>
                  </div>
                  <Button onClick={() => navigate(resolveLink(item))}>
                    See More
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryMethods;
